import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { useDrag } from "react-dnd";

import {
  Box,
  Container,
  Grid,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Chip,
  LinearProgress,
  StyledEngineProvider,
  ThemeProvider,
  useTheme
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import DoneIcon from "@mui/icons-material/Done";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortCircle } from "@fortawesome/pro-solid-svg-icons";

import { DefaultPaper } from "@aclymatepackages/atoms";
import { mergeDarkTheme, mainTheme } from "@aclymatepackages/themes";
import { formatDate, formatDecimal } from "@aclymatepackages/formatters";
import { editRowData } from "@aclymatepackages/array-immutability-helpers";
import { numbersRegExpTest } from "@aclymatepackages/reg-exp";
import TranslatedYesNoQuestion from "../../atoms/TranslatedYesNoQuestion";

import WorkspaceGraphicCard from "./WorkspaceGraphicCard";
import CommuteSchedulesInterface from "./CommuteSchedulesInterface";

import FormDivider from "../forms/FormDivider";
import MultiPartFormReveal from "../forms/MultiPartFormReveal";
import PlacesAutocomplete from "../autocomplete/PlacesAutocomplete";
import DbAutocomplete from "../autocomplete/DbAutocomplete";
import ElectricRenewablesYesNoQuestion from "../offices/ElectricRenewablesYesNoQuestion";
import ElectricRenewablesPercentageInput from "../offices/ElectricRenewablesPercentageInput";

import { useSharedNewEmployee } from "../../hooks/employees";
import DatePicker from "../../atoms/mui/DatePicker";
import RemovableRow from "../../modules/RemovableRow";

import {
  useSurveyBackendData,
  useWorkDayHeightPx
} from "../../../helpers/components/inputs";
import {
  useVehicleTypeProperties,
  alternateVehicles
} from "../../../helpers/components/vehicles";
import {
  useOfficeTypes,
  setOfficeAddress,
  electricRenewablesPercentageError
} from "../../../helpers/components/offices";
import {
  isObjectEmpty,
  generateTempId,
  useLayoutHelpers
} from "../../../helpers/otherHelpers";
import {
  fetchOurApi,
  fetchAddressEGrid
} from "../../../helpers/utils/apiCalls";
import { useSurveyLanguage } from "../../../helpers/contexts/surveyLanguage";

const itemTypes = { VEHICLE: "vehicle" };

const OfficeTypeQuestionRow = ({
  endDate,
  worksInThisOffice,
  setWorksInThisOffice,
  question,
  onYesSelect,
  onNoSelect,
  alternateQuestion,
  survey
}) => {
  const { translate, language } = useSurveyLanguage();
  const useYouForm = survey || language !== "en";

  const onButtonSelect = (value) => {
    if (!value && onNoSelect) {
      onNoSelect();
    }
    if (value && onYesSelect) {
      onYesSelect();
    }
    return setWorksInThisOffice(value);
  };

  const buildQuestionText = () => {
    if (useYouForm) {
      return translate("workspaces.duringAvgWeek", { endDate, question });
    }

    return `During an average week, ${
      endDate ? "did" : "does"
    } this employee work from ${question}?`;
  };

  return (
    <TranslatedYesNoQuestion
      question={alternateQuestion || buildQuestionText()}
      value={worksInThisOffice}
      setValue={onButtonSelect}
    />
  );
};

const CompanyOfficesSelect = ({
  companyOfficeEndpoints,
  addWorkspace,
  removeWorkspace,
  removeAllOfficesOfType,
  survey,
  helperText,
  companyOffices,
  officesLoading,
  error
}) => {
  const { translate, language } = useSurveyLanguage();
  const useYouForm = survey || language !== "en";
  const [availableOffices, setAvailableOffices] = useState([]);
  const [closedOffices, setClosedOffices] = useState([]);

  useEffect(() => {
    if (!officesLoading) {
      const notClosedOffices = companyOffices.filter(
        ({ isClosed }) => !isClosed
      );
      const closedOffices = companyOffices.filter(({ isClosed }) => isClosed);

      const availableOffices = notClosedOffices.filter(
        ({ id }) =>
          !companyOfficeEndpoints.find((endpoint) => endpoint.id === id)
      );
      setAvailableOffices(availableOffices);
      setClosedOffices(closedOffices);
    }
  }, [companyOfficeEndpoints, companyOffices, officesLoading]);

  const onOfficeSelect = (newValueArray) => {
    const [newOffice] = newValueArray.filter(
      ({ name }) =>
        !companyOfficeEndpoints.find((endpoint) => endpoint.name === name)
    );

    if (!newOffice) {
      return removeAllOfficesOfType("companyOffice");
    }

    return addWorkspace(newOffice);
  };

  const disableSelect =
    companyOffices?.length === companyOfficeEndpoints?.length;

  //TODO: these db autocompletes should be just regular autocompletes from @aclymatepackages/atoms since they aren't pulling from the db
  return (
    <DbAutocomplete
      multiple
      label={
        useYouForm
          ? translate("workspaces.selectOffices")
          : "Select company offices"
      }
      helperText={
        helperText ||
        (useYouForm
          ? translate("workspaces.selectOfficesHelper")
          : `Please select all of the offices this employee works in during an average week`)
      }
      FormHelperTextProps={{
        style: {
          color: helperText ? "red" : "inherit"
        }
      }}
      disabled={disableSelect}
      variant="standard"
      availableOptions={[...availableOffices, ...closedOffices]}
      value={companyOfficeEndpoints}
      setValue={onOfficeSelect}
      onDelete={({ tempId }) => removeWorkspace(tempId)}
      collection={"offices"}
      error={error}
    />
  );
};

const OtherOfficesInput = ({
  officeTypeEndpoints,
  addWorkspace,
  editWorkspace,
  removeWorkspace,
  label,
  placeholderLabel,
  addAnotherLabel
}) => {
  const moreButtonEnabled = officeTypeEndpoints.reduce(
    (acc, { address }) => acc && !!address,
    true
  );

  return (
    <Grid container direction="column" spacing={2}>
      {officeTypeEndpoints.map(({ address, tempId }) => (
        <RemovableRow
          key={`other-office-${tempId}`}
          rowCount={officeTypeEndpoints.length}
          removeRow={() => removeWorkspace(tempId)}
        >
          <Grid item sm={10} xs={12}>
            <PlacesAutocomplete
              place={address}
              editPlace={setOfficeAddress(editWorkspace(tempId), true)}
              label={placeholderLabel || `What is the ${label} Name or Address`}
              size="small"
              variant="standard"
            />
          </Grid>
        </RemovableRow>
      ))}
      <Grid item container justifyContent="center">
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={() => addWorkspace()}
            disabled={!moreButtonEnabled}
          >
            {addAnotherLabel}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const useDefineWorkspacesSteps = ({
  home = {},
  commuteEndpoints = [],
  editCommuteEndpoints,
  endDate,
  updatePeriodEndpoints,
  previousHome = {},
  editTimePeriod,
  editTimePeriodHomeProps,
  survey,
  isRemote
}) => {
  const { translate, language } = useSurveyLanguage();
  const useYouForm = survey || language !== "en";
  const {
    address: homeAddress,
    homeArea,
    homeOfficeArea,
    isElectricRenewablesPercentageGiven,
    electricRenewablesPercentage
  } = home;

  const {
    address: previousHomeAddress,
    homeArea: previousHomeArea,
    homeOfficeArea: previousHomeOfficeArea
  } = previousHome;

  const [companyOffices = [], officesLoading] = useSurveyBackendData("offices");

  const [worksInWorkspaceTypes, setWorksInWorkspaceTypes] = useState([]);
  const [
    isNotValidWorkspaceTypeQuestions,
    setIsNotValidWorkspaceTypeQuestions
  ] = useState(false);
  const [commuteOfficeHelperText, setCommuteOfficeHelperText] = useState("");

  useEffect(() => {
    const allAnswersAreNo = worksInWorkspaceTypes.every(
      (worksInWorkspaceType) => !worksInWorkspaceType.value
    );

    const setIsNotValidWorkspaceTypeQuestionsHandler = () => {
      if (worksInWorkspaceTypes.length === 4 && allAnswersAreNo) {
        return setIsNotValidWorkspaceTypeQuestions(true);
      }

      return setIsNotValidWorkspaceTypeQuestions(false);
    };

    setIsNotValidWorkspaceTypeQuestionsHandler();
  }, [worksInWorkspaceTypes]);

  const findSetWorkspaceType = (type, array = worksInWorkspaceTypes) =>
    array.find((workspace) => workspace.type === type);

  const editWorksInWorkspaceType = (type) => (value) =>
    setWorksInWorkspaceTypes((currentTypesArray) => {
      const typeAlreadyExists = !!findSetWorkspaceType(type, currentTypesArray);

      if (!typeAlreadyExists) {
        return [...currentTypesArray, { type, value }];
      }

      const filteredTypesArray = currentTypesArray.filter(
        (workspace) => workspace.type !== type
      );
      return [...filteredTypesArray, { type, value }];
    });

  const addWorkspace = (type) => async (newOfficeObj) => {
    editCommuteEndpoints([
      ...commuteEndpoints,
      {
        tempId: generateTempId(),
        type,
        ...newOfficeObj
      }
    ]);

    if (!newOfficeObj) {
      return;
    }

    const { totalMileage: oneWayMileage } = await fetchOurApi({
      path: "/calcs/cars/directions-mileage",
      method: "POST",
      data: {
        to: newOfficeObj.address,
        from: homeAddress
      },
      callback: (res) => res
    });

    if (oneWayMileage < 100) {
      return;
    }

    return setCommuteOfficeHelperText(
      useYouForm
        ? translate("workspaces.officeFarAway", {
            miles: formatDecimal(oneWayMileage)
          })
        : `An office you selected is ${formatDecimal(
            oneWayMileage
          )} miles away from this employee's home.`
    );
  };

  const editWorkspace = (tempId) => (field) => (value) => {
    const endpointsUpdater = (commuteEndpoints) =>
      commuteEndpoints.map((endpoint) => {
        if (endpoint.tempId !== tempId) {
          return endpoint;
        }
        return { ...endpoint, [field]: value };
      });

    return updatePeriodEndpoints(endpointsUpdater);
  };

  const removeWorkspace = (tempId) => {
    tempId &&
      editCommuteEndpoints(
        commuteEndpoints.filter((workspace) => workspace.tempId !== tempId)
      );

    return setCommuteOfficeHelperText("");
  };

  const filterEndpointsByCategory = (filterCategory) =>
    commuteEndpoints.filter(({ type }) => type === filterCategory);

  const removeAllOfficesOfType = (type) =>
    editCommuteEndpoints(
      commuteEndpoints.filter((endpoint) => endpoint.type !== type)
    );

  const buildCompanyOfficesRow = () => {
    if (isRemote) {
      return [];
    }

    const formatCompanyOfficeQuestion = () => {
      if (useYouForm) {
        return translate("workspaces.companyOffice", {
          count: companyOffices.length
        });
      }

      if (companyOffices.length === 1) {
        return "your company office";
      }
      return "any of your company offices";
    };

    const onYesSelect = () => {
      if (companyOffices.length > 1) {
        return;
      }
      const [office] = companyOffices;
      return addWorkspace("companyOffice")(office);
    };

    const inputRows =
      companyOffices.length > 1
        ? [
            {
              input: (
                <CompanyOfficesSelect
                  companyOfficeEndpoints={filterEndpointsByCategory(
                    "companyOffice"
                  )}
                  addWorkspace={addWorkspace("companyOffice")}
                  removeWorkspace={removeWorkspace}
                  removeAllOfficesOfType={removeAllOfficesOfType}
                  survey={survey}
                  helperText={commuteOfficeHelperText}
                  companyOffices={companyOffices}
                  officesLoading={officesLoading}
                  error={!!commuteOfficeHelperText}
                />
              ),
              value: !!filterEndpointsByCategory("companyOffice").length
            }
          ]
        : [];

    return [
      {
        type: "companyOffice",
        question: formatCompanyOfficeQuestion(),
        onNoSelect: () => removeAllOfficesOfType("companyOffice"),
        onYesSelect,
        inputRows
      }
    ];
  };

  const buildHomeOfficeInputRows = () => {
    const [homeOffice] = filterEndpointsByCategory("homeOffice");
    const { tempId } = homeOffice || {};

    const homeAreaCompBool =
      !!homeOfficeArea &&
      !!homeArea &&
      Number(homeOfficeArea) >= Number(homeArea);

    const addNewHomeAddress = () => (address) =>
      editTimePeriodHomeProps("address")(address);

    const electricRenewablesPercentageInput =
      isElectricRenewablesPercentageGiven
        ? [
            {
              input: (
                <ElectricRenewablesPercentageInput
                  electricRenewablesPercentage={electricRenewablesPercentage}
                  editElectricRenewablesPercentage={(value) =>
                    editTimePeriodHomeProps("electricRenewablesPercentage")(
                      value
                    )
                  }
                  variant="standard"
                  label={
                    useYouForm
                      ? translate("workspaces.renewablePercentage")
                      : "What % of electricity is from renewables?"
                  }
                />
              ),
              value:
                electricRenewablesPercentage &&
                !electricRenewablesPercentageError(electricRenewablesPercentage)
            }
          ]
        : [];

    const areaInputRows = [
      {
        label: useYouForm
          ? translate("workspaces.homeSize")
          : "How big is this home?",
        helperText: useYouForm
          ? translate("workspaces.sqFootageHelper")
          : "Please enter a square footage",
        value: homeArea,
        editData: editTimePeriodHomeProps("homeArea"),
        error: homeAreaCompBool || (homeArea && !numbersRegExpTest(homeArea))
      },
      {
        label: useYouForm
          ? translate("workspaces.homeOfficeSize")
          : "How big is this home office?",
        helperText: useYouForm
          ? translate("workspaces.sqFootageHelper")
          : "Please enter a square footage",
        value: homeOfficeArea,
        editData: editTimePeriodHomeProps("homeOfficeArea"),
        error:
          homeAreaCompBool ||
          (homeOfficeArea && !numbersRegExpTest(homeOfficeArea))
      },
      {
        input: (
          <ElectricRenewablesYesNoQuestion
            questionText={
              useYouForm
                ? translate("workspaces.renewableEnergy")
                : "Is this home powered by renewable energy sources?"
            }
            isElectricRenewablesPercentageGiven={
              isElectricRenewablesPercentageGiven
            }
            editIsElectricRenewablesPercentageGiven={(value) =>
              editTimePeriodHomeProps("isElectricRenewablesPercentageGiven")(
                value
              )
            }
          />
        ),
        value: isElectricRenewablesPercentageGiven !== undefined
      },
      ...electricRenewablesPercentageInput
    ];

    if (endDate) {
      const didHomeAddressChange = !worksInWorkspaceTypes.find(
        ({ type }) => type === "newHomeOffice"
      )?.value;

      const newHomeOfficeInputRows =
        didHomeAddressChange || !previousHomeArea || !previousHomeOfficeArea
          ? { inputRows: areaInputRows }
          : {};

      const otherHomeAddressInputRows =
        homeAddress !== previousHomeAddress
          ? {
              inputRows: [
                {
                  input: (
                    <PlacesAutocomplete
                      place={homeAddress}
                      editPlace={setOfficeAddress(addNewHomeAddress)}
                      label={
                        useYouForm
                          ? translate("workspaces.otherHomeAddress")
                          : "What is the other home's address?"
                      }
                      size="small"
                      variant="standard"
                    />
                  ),
                  value: homeAddress
                }
              ]
            }
          : {};

      return [
        {
          alternateQuestion: useYouForm
            ? translate("workspaces.previousHomeQuestion", {
                address: previousHomeAddress.description
              })
            : `During this time, did this employee still live at ${previousHomeAddress.description}?`,
          type: "newHomeOffice",
          onYesSelect: () =>
            editTimePeriodHomeProps("address")(previousHomeAddress),
          onNoSelect: () => editTimePeriodHomeProps("address")(null),
          ...otherHomeAddressInputRows
        },
        {
          type: "homeOffice",
          question: useYouForm
            ? translate("workspaces.homeOffice")
            : "their home office",
          onYesSelect: () => {
            if (!didHomeAddressChange) {
              editTimePeriod("home")(previousHome);
            }
            return addWorkspace("homeOffice")();
          },
          onNoSelect: () => removeWorkspace(tempId),
          ...newHomeOfficeInputRows
        }
      ];
    }

    return [
      {
        type: "homeOffice",
        question: useYouForm
          ? translate("workspaces.homeOffice")
          : "their home office",
        onYesSelect: () => addWorkspace("homeOffice")(),
        onNoSelect: () => removeWorkspace(tempId),
        inputRows: areaInputRows
      }
    ];
  };

  const otherOfficeConfig = [
    {
      type: "coffeeShop",
      question: useYouForm
        ? translate("workspaces.coffeeShops")
        : "any coffee shops",
      label: useYouForm
        ? translate("workspaces.coffeeShopLabel")
        : "Coffee Shop",
      placeholderLabel: useYouForm
        ? translate("workspaces.coffeeShopPlaceholder")
        : "What is the Coffee Shop Name or Address",
      addAnotherLabel: useYouForm
        ? translate("workspaces.addAnother", {
            label: translate("workspaces.coffeeShopLabel")
          })
        : "Add another Coffee Shop where this employee works"
    },
    {
      type: "coworking",
      question: useYouForm
        ? translate("workspaces.coworkingSpaces")
        : "any coworking spaces",
      label: useYouForm
        ? translate("workspaces.coworkingLabel")
        : "Coworking Space",
      placeholderLabel: useYouForm
        ? translate("workspaces.coworkingPlaceholder")
        : "What is the Coworking Space Name or Address",
      addAnotherLabel: useYouForm
        ? translate("workspaces.addAnother", {
            label: translate("workspaces.coworkingLabel")
          })
        : "Add another Coworking Space where this employee works"
    }
  ];

  const buildOtherOfficeRows = () =>
    otherOfficeConfig.map(
      ({ label, type, question, placeholderLabel, addAnotherLabel }) => ({
        type,
        question,
        onYesSelect: () => addWorkspace(type)(),
        onNoSelect: () => removeAllOfficesOfType(type),
        inputRows: [
          {
            input: (
              <OtherOfficesInput
                label={label}
                placeholderLabel={placeholderLabel}
                addAnotherLabel={addAnotherLabel}
                officeTypeEndpoints={filterEndpointsByCategory(type)}
                editWorkspace={editWorkspace}
                removeWorkspace={removeWorkspace}
                addWorkspace={addWorkspace(type)}
              />
            ),
            value: filterEndpointsByCategory(type).reduce(
              (acc, { address }) => acc && !!address,
              true
            )
          }
        ]
      })
    );

  const workspaceTypeQuestions = [
    ...buildCompanyOfficesRow(),
    ...buildHomeOfficeInputRows(),
    ...buildOtherOfficeRows()
  ];

  const buildInputRowsArray = (question) => {
    const { type, inputRows = [] } = question;
    const isWorkspaceTypeSet = findSetWorkspaceType(type);
    const { value: worksInThisOffice } = isWorkspaceTypeSet || {};

    if ((type === "newHomeOffice" && !worksInThisOffice) || worksInThisOffice) {
      return inputRows;
    }

    return [];
  };

  const workspaceQuestions = workspaceTypeQuestions.flatMap((question) => {
    const { type } = question;
    const isWorkspaceTypeSet = findSetWorkspaceType(type);

    const { value: worksInThisOffice } = isWorkspaceTypeSet || {};

    const questionRowProps = {
      ...question,
      worksInThisOffice,
      setWorksInThisOffice: editWorksInWorkspaceType(type),
      survey
    };

    const inputRowsArray = buildInputRowsArray(question);
    return [
      {
        input: (
          <OfficeTypeQuestionRow endDate={endDate} {...questionRowProps} />
        ),
        value: isWorkspaceTypeSet
      },
      ...inputRowsArray
    ];
  });

  return { workspaceQuestions, isNotValidWorkspaceTypeQuestions };
};

const WorkspaceScheduleBlock = ({
  type,
  daysPerWk,
  color,
  children,
  ...otherProps
}) => {
  const { color: officeColor } = useOfficeTypes({ type });
  const workDayHeightPx = useWorkDayHeightPx();

  return (
    <Box
      p={1}
      style={{
        backgroundColor: color || officeColor,
        color: "white",
        borderRadius: "5px",
        border: `thin solid white`,
        boxSizing: "border-box",
        width: "100%",
        height: `${daysPerWk * workDayHeightPx}px`
      }}
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="relative"
      {...otherProps}
    >
      {children}
    </Box>
  );
};

const WorkweekScheduleBlock = ({ workSchedule, setWorkSchedule }) => {
  const { isMobile } = useLayoutHelpers();
  const workDayHeightPx = useWorkDayHeightPx();

  // Simple local drag state
  const [dragState, setDragState] = useState({
    isDragging: false,
    startY: null,
    startDays: null
  });

  const handleDragStart = (e) => {
    const clientY = isMobile ? e.touches[0].clientY : e.clientY;
    const weekendBlock = workSchedule[0]; // Weekend is first
    const workBlock = workSchedule[1]; // Work week is second

    setDragState({
      isDragging: true,
      startY: clientY,
      startDays: {
        weekend: weekendBlock.daysPerWk,
        workweek: workBlock.daysPerWk
      }
    });

    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragMove = (e) => {
    if (!dragState.isDragging) return;

    const clientY = isMobile ? e.touches[0].clientY : e.clientY;
    const deltaY = dragState.startY - clientY;
    const quarterDayHeight = workDayHeightPx / 4;
    const daysDelta = Math.round(deltaY / quarterDayHeight) * 0.25;

    const newWorkweekDays = dragState.startDays.workweek + daysDelta;
    const newWeekendDays = dragState.startDays.weekend - daysDelta;

    // Keep within bounds (0.25 to 6.75 work days)
    if (
      newWorkweekDays >= 0.25 &&
      newWorkweekDays <= 6.75 &&
      newWeekendDays >= 0.25
    ) {
      setWorkSchedule([
        { ...workSchedule[0], daysPerWk: newWeekendDays }, // Weekend
        { ...workSchedule[1], daysPerWk: newWorkweekDays } // Work week
      ]);
    }
  };

  const handleDragEnd = () => {
    setDragState({
      isDragging: false,
      startY: null,
      startDays: null
    });
  };

  const dragProps = isMobile
    ? { onTouchMove: handleDragMove, onTouchEnd: handleDragEnd }
    : {
        onMouseMove: handleDragMove,
        onMouseUp: handleDragEnd,
        onMouseLeave: handleDragEnd
      };

  const arrowDraggingProps = isMobile
    ? { onTouchStart: handleDragStart, onTouchEnd: handleDragEnd }
    : { onMouseDown: handleDragStart, onMouseUp: handleDragEnd };

  return (
    <Box display="flex" flexDirection="column" {...dragProps}>
      {workSchedule.map(({ name, daysPerWk, color }, idx) => (
        <WorkspaceScheduleBlock
          key={`work-week-schedule-block-${idx}`}
          color={color}
          daysPerWk={daysPerWk}
        >
          <Typography variant="h6">{`${name}- ${daysPerWk} days/week`}</Typography>
          {!!idx && (
            <div
              style={{
                position: "absolute",
                top: "-40px",
                left: "calc(50% - 36px)",
                zIndex: 1500
              }}
            >
              <IconButton {...arrowDraggingProps} size="large">
                <FontAwesomeIcon
                  icon={faSortCircle}
                  style={{ color: "white" }}
                  size="2x"
                />
              </IconButton>
            </div>
          )}
        </WorkspaceScheduleBlock>
      ))}
    </Box>
  );
};

const SlideArrow = ({ onArrowSelect, onArrowDeselect }) => {
  const { isMobile } = useLayoutHelpers();

  const draggingProps = isMobile
    ? {
        onTouchStart: onArrowSelect,
        onTouchEnd: onArrowDeselect
      }
    : {
        onMouseDown: onArrowSelect,
        onMouseUp: onArrowDeselect
      };

  return (
    <div
      style={{
        position: "absolute",
        top: "-28px",
        left: "calc(50% - 24px)",
        zIndex: 1500
      }}
    >
      <IconButton {...draggingProps} size="large">
        <FontAwesomeIcon
          icon={faSortCircle}
          style={{ color: "white" }}
          size="1x"
        />
      </IconButton>
    </div>
  );
};

const WorkspaceDaysPerWkBlock = ({
  type,
  daysPerWk,
  name,
  isDraggable,
  onOfficeSelect,
  onOfficeDeselect
}) => {
  const { translate } = useSurveyLanguage();

  return (
    <WorkspaceScheduleBlock type={type} daysPerWk={daysPerWk}>
      {isDraggable && (
        <SlideArrow
          onArrowSelect={onOfficeSelect}
          onArrowDeselect={onOfficeDeselect}
        />
      )}
      <Typography variant="subtitle2" color="inherit" align="center">{`${
        type === "homeOffice" ? translate("workspaces.homeOfficeLabel") : name
      }- ${daysPerWk} days/wk.`}</Typography>
    </WorkspaceScheduleBlock>
  );
};

const WorkspaceScheduleColumn = ({
  weekendDays,
  children,
  editMode,
  ...otherProps
}) => {
  const { palette } = useTheme();
  const { translate } = useSurveyLanguage();

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-end"
      style={{ height: "100%" }}
      {...otherProps}
    >
      <WorkspaceScheduleBlock
        color={palette["business-travel"].main}
        daysPerWk={weekendDays}
      >
        <Typography variant="h6" align="center">
          {translate("schedule.weekend")}
        </Typography>
      </WorkspaceScheduleBlock>
      {children}
    </Box>
  );
};

export const DraggableOfficesGraphic = ({
  commuteEndpoints,
  editCommuteEndpoints
}) => {
  const { isMobile } = useLayoutHelpers();
  const workDayHeightPx = useWorkDayHeightPx();

  // Simple local drag state - no external hook
  const [dragState, setDragState] = useState({
    isDragging: false,
    dragIndex: null,
    startY: null,
    startDays: null
  });

  const totalWorkweekDays = commuteEndpoints.reduce(
    (sum, { daysPerWk }) => Number(sum) + Number(daysPerWk || 0),
    0
  );

  const handleDragStart = (index) => (e) => {
    const clientY = isMobile ? e.touches[0].clientY : e.clientY;
    const current = commuteEndpoints[index];
    const adjacent = index === 0 ? null : commuteEndpoints[index - 1];

    setDragState({
      isDragging: true,
      dragIndex: index,
      startY: clientY,
      startDays: {
        current: current.daysPerWk,
        adjacent: adjacent ? adjacent.daysPerWk : 7 - totalWorkweekDays
      }
    });

    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragMove = (e) => {
    if (!dragState.isDragging || dragState.dragIndex === null) return;

    const clientY = isMobile ? e.touches[0].clientY : e.clientY;
    const deltaY = dragState.startY - clientY;
    const quarterDayHeight = workDayHeightPx / 4;
    const daysDelta = Math.round(deltaY / quarterDayHeight) * 0.25;

    const newCurrentDays = dragState.startDays.current + daysDelta;
    const newAdjacentDays = dragState.startDays.adjacent - daysDelta;

    if (newCurrentDays >= 0.25 && newAdjacentDays >= 0) {
      const updatedEndpoints = commuteEndpoints.map((endpoint, idx) => {
        if (idx === dragState.dragIndex) {
          return { ...endpoint, daysPerWk: newCurrentDays };
        }
        if (idx === dragState.dragIndex - 1) {
          return { ...endpoint, daysPerWk: newAdjacentDays };
        }
        return endpoint;
      });

      editCommuteEndpoints(updatedEndpoints);
    }
  };

  const handleDragEnd = () => {
    setDragState({
      isDragging: false,
      dragIndex: null,
      startY: null,
      startDays: null
    });
  };

  const dragProps = isMobile
    ? { onTouchMove: handleDragMove, onTouchEnd: handleDragEnd }
    : {
        onMouseMove: handleDragMove,
        onMouseUp: handleDragEnd,
        onMouseLeave: handleDragEnd
      };

  return (
    <WorkspaceScheduleColumn
      weekendDays={7 - totalWorkweekDays}
      editMode={false}
      {...dragProps}
    >
      {commuteEndpoints.map((office, idx) => (
        <WorkspaceDaysPerWkBlock
          key={`draggable-office-block-${office.tempId}`}
          isDraggable={!!idx}
          {...office}
          onOfficeSelect={handleDragStart(idx)}
          onOfficeDeselect={handleDragEnd}
        />
      ))}
    </WorkspaceScheduleColumn>
  );
};

const AvailableCommuteVehicleChip = ({
  type,
  name,
  description,
  tonsCo2ePerMile,
  id
}) => {
  const isTouchBackend = "ontouchstart" in window;
  const { isMobile } = useLayoutHelpers();

  const { color, icon, tooltip } = useVehicleTypeProperties(type) || {};

  const [{ isDragging, offset }, drag] = useDrag(() => ({
    type: itemTypes.VEHICLE,
    item: { id, type, name: name || description, tonsCo2ePerMile },
    collect: (monitor) => {
      const isDragging = !!monitor.isDragging();

      if (isDragging && isTouchBackend) {
        const offset = monitor.getDifferenceFromInitialOffset() || {
          x: 0,
          y: 0
        };
        return { isDragging, offset };
      }

      return { isDragging };
    }
  }));
  const { x: offsetX = 0, y: offsetY = 0 } = offset || {};

  const chipProps = {
    size: isMobile ? "small" : "medium",
    avatar: (
      <Tooltip title={tooltip}>
        <Avatar
          style={{
            backgroundColor: color,
            color: "white"
          }}
        >
          <FontAwesomeIcon icon={icon} size="sm" />
        </Avatar>
      </Tooltip>
    ),
    label: name || description
  };

  return (
    <Grid item>
      {isTouchBackend && isDragging && (
        <div style={{ position: "relative" }}>
          <Chip
            {...chipProps}
            style={{
              position: "absolute",
              top: `${offsetY}px`,
              left: `${offsetX}px`
            }}
          />
        </div>
      )}
      <Chip
        ref={drag}
        {...chipProps}
        style={{
          cursor: isDragging ? "grabbing" : "grab"
        }}
      />
    </Grid>
  );
};

const DragVehiclesStep = ({ survey }) => {
  const { isMobile } = useLayoutHelpers();
  const { translate, language } = useSurveyLanguage();
  const useYouForm = survey || language !== "en";
  const { employeeVehicles } = useSharedNewEmployee();
  const [companyVehicles, companyVehiclesLoading] =
    useSurveyBackendData("vehicles");

  const vehiclesRows = [
    {
      label: useYouForm
        ? translate("commuteVehicles.yourVehicles")
        : "Employee Vehicles",
      annotation: useYouForm
        ? translate("commuteVehicles.yourVehiclesAnnotation")
        : "These are vehicles owned by this employee that you defined earlier in this form. You can go back to that step if you need to edit these vehicles.",
      vehicles: employeeVehicles
        .filter((vehicle) => !isObjectEmpty(vehicle) && !vehicle.archived)
        .map((vehicle) => ({
          ...vehicle,
          type: "personal",
          name: vehicle.description || vehicle.name
        }))
    },
    {
      label: useYouForm
        ? translate("commuteVehicles.companyVehicles")
        : "Company Vehicles",
      annotation: useYouForm
        ? translate("commuteVehicles.companyVehiclesAnnotation")
        : "These are vehicles that are owned and managed by your company that can be edited on your company vehicles page.",
      vehicles: companyVehicles,
      isLoading: companyVehiclesLoading
    },
    {
      label: useYouForm
        ? translate("commuteVehicles.alternateTransportation")
        : "Alternate Transportation",
      annotation: useYouForm
        ? translate("commuteVehicles.alternateAnnotation")
        : "These are alternate forms of transportation that don't involve driving a vehicle to work.",
      vehicles: alternateVehicles.map((vehicle) => ({
        ...vehicle,
        name: useYouForm
          ? translate(
              {
                walkBike: "vehicles.altWalkBike",
                lightRail: "vehicles.altLightRail",
                regionalRail: "vehicles.altRegionalRail",
                intercityRail: "vehicles.altIntercityRail",
                carpool: "vehicles.altCarpool",
                bus: "vehicles.altBus"
              }[vehicle.type] || "vehicles.altWalkBike"
            )
          : vehicle.name
      }))
    }
  ];

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <Typography variant="body1">
          {useYouForm
            ? translate("commuteVehicles.instructions")
            : `Drag a vehicle from the options below into each office and then drag it like you dragged the offices to adjust how many days per week they commute to that office with that vehicle.`}
        </Typography>
      </Grid>
      <Grid item container spacing={isMobile ? 2 : 3}>
        {vehiclesRows
          .filter(({ vehicles }) => vehicles?.length)
          .map(({ label, annotation, vehicles, isLoading }, rowIdx) => (
            <Grid
              item
              container
              direction="column"
              spacing={isMobile ? 0 : 1}
              key={`vehicles-chips-row-${rowIdx}`}
            >
              <Grid item container justifyContent="space-between">
                <Grid item>
                  <Typography variant="subtitle1">{label}</Typography>
                </Grid>
                <Grid item>
                  <Tooltip title={annotation}>
                    <HelpOutlineIcon />
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid item container spacing={1}>
                {isLoading ? (
                  <LinearProgress />
                ) : !vehicles.length ? (
                  <Typography align="center" variant="body1">
                    {useYouForm
                      ? translate("commuteVehicles.noVehiclesAvailable")
                      : `There are no ${label.toLowerCase()} available`}
                  </Typography>
                ) : (
                  vehicles.map((vehicle, idx) => (
                    <AvailableCommuteVehicleChip
                      key={`available-commute-vehicle-chip-${idx}`}
                      {...vehicle}
                    />
                  ))
                )}
              </Grid>
            </Grid>
          ))}
      </Grid>
    </Grid>
  );
};

const useTimePeriodTransitionStep = ({
  onSubmit,
  editTimePeriod,
  startDate,
  addNewTimePeriod,
  endDate,
  survey,
  newCommuteScheduleSurvey,
  employeeCommuteSurvey,
  companyStartDate
}) => {
  const { newEmployee } = useSharedNewEmployee();
  const { translate, language } = useSurveyLanguage();
  const useYouForm = survey || language !== "en";
  const { startDate: employeeStartDate, commuteSchedules = [{}] } = newEmployee;
  const [{ startDate: currentScheduleStartDate }] = commuteSchedules;

  const [showNextStep, setShowTimePeriodDate] = useState(
    newCommuteScheduleSurvey ? true : false
  );

  const buildTitle = () =>
    !newCommuteScheduleSurvey
      ? useYouForm
        ? translate("startDate.workedSince", {
            date: formatDate(employeeStartDate || companyStartDate)
          })
        : `Has this employee worked this schedule since at least ${formatDate(
            employeeStartDate || companyStartDate
          )}.`
      : translate("startDate.label");

  return employeeCommuteSurvey
    ? {
        label: translate("startDate.label"),
        input: (
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={mainTheme}>
              <DatePicker
                label={
                  useYouForm
                    ? translate("startDate.whenDidYouStartWorking")
                    : "When did you start working with this schedule?"
                }
                date={startDate}
                minDate={
                  currentScheduleStartDate ||
                  employeeStartDate ||
                  companyStartDate
                }
                editDate={editTimePeriod("startDate")}
                darkTheme
              />
              <Grid container justifyContent="center">
                <Grid item style={{ marginTop: "1rem" }}>
                  <Button
                    color="success"
                    endIcon={<CheckIcon />}
                    onClick={() => onSubmit()}
                    variant="contained"
                  >
                    {translate("common.submit")}
                  </Button>
                </Grid>
              </Grid>
            </ThemeProvider>
          </StyledEngineProvider>
        ),
        noButton: true
      }
    : {
        label: translate("startDate.label"),
        title: buildTitle(),
        input: (
          <>
            {showNextStep ? (
              <DatePicker
                label={
                  useYouForm
                    ? translate("startDate.whenDidYouStart")
                    : `When did this employee start this schedule?`
                }
                date={startDate}
                maxDate={endDate || new Date()}
                minDate={
                  currentScheduleStartDate ||
                  employeeStartDate ||
                  companyStartDate
                }
                editDate={editTimePeriod("startDate")}
                inputVariant="standard"
                darkTheme
              />
            ) : (
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={mainTheme}>
                  <Grid container spacing={2} justifyContent="center">
                    <Grid item>
                      <Button
                        color="primary"
                        endIcon={<CheckIcon />}
                        onClick={() => onSubmit()}
                        variant="contained"
                      >
                        {translate("common.yes")}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        color="secondary"
                        endIcon={<CancelIcon />}
                        variant="contained"
                        onClick={() => setShowTimePeriodDate(true)}
                      >
                        {translate("common.no")}
                      </Button>
                    </Grid>
                  </Grid>
                </ThemeProvider>
              </StyledEngineProvider>
            )}
          </>
        ),
        noButton: !showNextStep,
        onAdvanceForm: newCommuteScheduleSurvey ? onSubmit : addNewTimePeriod
      };
};

const NewEmployeeWorkspacesGraphic = ({
  daysPerWk,
  graphicStep,
  commuteEndpoints,
  editCommuteEndpoints,
  editCommuteEndpointsDirectly,
  updatePeriodEndpoints,
  endDate,
  workSchedule,
  setWorkSchedule,
  home,
  editTimePeriodHome,
  editTimePeriod
}) => {
  const { newEmployee, employeeVehicles } = useSharedNewEmployee();

  const graphicSteps = [
    <WorkweekScheduleBlock
      workSchedule={workSchedule}
      setWorkSchedule={setWorkSchedule}
    />,
    <DraggableOfficesGraphic
      commuteEndpoints={commuteEndpoints}
      editCommuteEndpoints={editCommuteEndpointsDirectly}
    />,
    <CommuteSchedulesInterface
      commuteEndpoints={commuteEndpoints}
      editCommuteEndpoints={editCommuteEndpoints}
      editTimePeriod={editTimePeriod}
      updatePeriodEndpoints={updatePeriodEndpoints}
      editCommuteScheduleHome={editTimePeriodHome}
      editMode
      commuteHome={home}
      employeePersonalVehicles={employeeVehicles}
    />
  ];

  return (
    <WorkspaceGraphicCard
      employeeName={newEmployee.name}
      endDate={endDate}
      daysPerWk={daysPerWk}
      graphic={graphicSteps[graphicStep - 1]}
    />
  );
};

const TimePeriodGraphicForm = ({
  daysPerWk,
  startDate,
  endDate,
  commuteEndpoints = [],
  home,
  onSubmit,
  editTimePeriod,
  addNewTimePeriod,
  isActivePeriod,
  updatePeriodEndpoints,
  editTimePeriodHomeProps,
  editTimePeriodHome,
  previousPeriod,
  survey,
  newCommuteScheduleSurvey,
  employeeCommuteSurvey,
  companyStartDate,
  isRemote
}) => {
  const theme = useTheme();
  const nonHomeEndpoints = commuteEndpoints.filter(
    ({ type }) => type !== "homeOffice"
  );

  const { translate, language } = useSurveyLanguage();
  const useYouForm = survey || language !== "en";

  const [formOpen, setFormOpen] = useState(false);
  const [graphicStep, setGraphicStep] = useState(0);
  const [workSchedule, setWorkSchedule] = useState([
    {
      name: useYouForm ? translate("schedule.weekend") : "Weekend",
      tempId: generateTempId(),
      daysPerWk: daysPerWk ? 7 - daysPerWk : 2,
      color: theme.palette["business-travel"].main,
      label: "weekend"
    },
    {
      name: useYouForm ? translate("schedule.workWeek") : "Work week",
      tempId: generateTempId(),
      daysPerWk: daysPerWk || 5,
      color: theme.palette.employees.main,
      label: "workWeek"
    }
  ]);

  const onAdvanceGraphicStep = () =>
    setGraphicStep((currentStep) => {
      const newGraphicStep = currentStep + 1;

      return newGraphicStep > 3 ? currentStep : newGraphicStep;
    });

  // Helper to proportionally adjust vehicles to a new endpoint daysPerWk
  const calculateNewVehiclesDaysPerWk = (
    vehicles = [],
    oldDaysPerWk = 0,
    newDaysPerWk = 0
  ) => {
    if (!vehicles.length) return [];
    const adjustedDaysPerWk = (newDaysPerWk || 0) - (oldDaysPerWk || 0) || 0;
    const largestDaysPerWkVehicle = vehicles.reduce((max, vehicle) =>
      vehicle.daysPerWk > max.daysPerWk ? vehicle : max
    );
    const newVehiclesCorrectDaysPerWk = vehicles.map((vehicle) =>
      vehicle.id === largestDaysPerWkVehicle.id
        ? { ...vehicle, daysPerWk: vehicle.daysPerWk + adjustedDaysPerWk }
        : vehicle
    );

    return newVehiclesCorrectDaysPerWk.filter(
      ({ daysPerWk }) => daysPerWk >= 0.25
    );
  };

  // Evenly distribute total work days across endpoints with quarter-day resolution
  const buildEvenDaysDistribution = (endpointsCount, totalDays) => {
    if (!endpointsCount) return [];
    const divided = (totalDays || 0) / endpointsCount;
    const others = [...new Array(Math.max(0, endpointsCount - 1))].map(
      () => Math.ceil(divided * 4) / 4
    );
    const othersSum = others.reduce(
      (sum, curr) => Number(sum) + Number(curr),
      0
    );
    const first = Number(((totalDays || 0) - othersSum).toFixed(2));
    return [first, ...others];
  };

  // Normalize endpoint days-per-week when the list of endpoints changes or is invalid
  const normalizeCommuteEndpoints = (incomingEndpoints) => {
    const targetWorkDays =
      typeof daysPerWk === "number" ? daysPerWk : undefined;
    if (!incomingEndpoints?.length || targetWorkDays === undefined) {
      return incomingEndpoints;
    }

    const missingDays = incomingEndpoints.some(
      (ep) => typeof ep.daysPerWk !== "number" || Number.isNaN(ep.daysPerWk)
    );
    const sumIncoming = incomingEndpoints.reduce(
      (sum, ep) => Number(sum) + Number(ep.daysPerWk || 0),
      0
    );

    const needsNormalize =
      missingDays || Math.abs(sumIncoming - targetWorkDays) > 0.001;

    if (!needsNormalize) {
      return incomingEndpoints;
    }

    const daysDistribution = buildEvenDaysDistribution(
      incomingEndpoints.length,
      targetWorkDays
    );

    return incomingEndpoints.map((endpoint, idx) => {
      const newDays = daysDistribution[idx] || 0;
      const prev =
        (commuteEndpoints || []).find((e) => e.tempId === endpoint.tempId) ||
        {};
      const vehicles = prev.vehicles || endpoint.vehicles;
      const vehiclesAdjusted =
        vehicles && vehicles.length
          ? calculateNewVehiclesDaysPerWk(vehicles, prev.daysPerWk, newDays)
          : undefined;
      return {
        ...endpoint,
        daysPerWk: newDays,
        ...(vehiclesAdjusted ? { vehicles: vehiclesAdjusted } : {})
      };
    });
  };

  const editCommuteEndpoints = (newEndpoints) =>
    editTimePeriod("commuteEndpoints")(normalizeCommuteEndpoints(newEndpoints));

  // Version for drag operations that bypasses normalization to avoid re-renders
  const editCommuteEndpointsDirectly = (newEndpoints) =>
    editTimePeriod("commuteEndpoints")(newEndpoints);

  const buildFormTitle = () => {
    if (startDate && endDate) {
      return useYouForm
        ? translate("timePeriod.fromTo", {
            startDate: formatDate(startDate),
            endDate: formatDate(endDate)
          })
        : `Commute Schedule from ${formatDate(startDate)} to ${formatDate(
            endDate
          )}`;
    }

    if (startDate) {
      return useYouForm
        ? translate("timePeriod.fromToNow", {
            startDate: formatDate(startDate)
          })
        : `Commute Schedule from ${formatDate(startDate)} to now`;
    }

    if (endDate) {
      return useYouForm
        ? translate("timePeriod.priorTo", { endDate: formatDate(endDate) })
        : `Commute Schedule prior to ${formatDate(endDate)}`;
    }

    return useYouForm ? translate("timePeriod.default") : "Commute Schedule";
  };

  const onDaysPerWeekNextStep = () => {
    const conditionallyAdvanceGraphicStep = () => {
      if (commuteEndpoints.length > 1) {
        return onAdvanceGraphicStep();
      }
      return setGraphicStep(3);
    };
    conditionallyAdvanceGraphicStep();

    const { daysPerWk: adjustedDaysPerWk } = workSchedule.find(
      ({ label }) => label === "workWeek"
    );
    editTimePeriod("daysPerWk")(adjustedDaysPerWk);

    const endpointsCount = commuteEndpoints.length;
    if (endpointsCount === 1) {
      return editCommuteEndpoints(
        commuteEndpoints.map((endpoint) => ({
          ...endpoint,
          daysPerWk: adjustedDaysPerWk
        }))
      );
    }

    const daysPerWkArray = buildEvenDaysDistribution(
      endpointsCount,
      adjustedDaysPerWk
    );

    return editCommuteEndpoints(
      commuteEndpoints.map(({ timePeriods, ...endpoint }, idx) => ({
        ...endpoint,
        daysPerWk: daysPerWkArray[idx]
      }))
    );
  };

  const { workspaceQuestions, isNotValidWorkspaceTypeQuestions } =
    useDefineWorkspacesSteps({
      commuteEndpoints,
      editCommuteEndpoints,
      endDate,
      updatePeriodEndpoints,
      previousHome: previousPeriod?.home,
      editTimePeriod,
      editTimePeriodHomeProps,
      home,
      survey,
      isRemote
    });

  const timePeriodTransitionStep = useTimePeriodTransitionStep({
    onSubmit,
    editTimePeriod,
    startDate,
    addNewTimePeriod,
    endDate,
    survey,
    newCommuteScheduleSurvey,
    employeeCommuteSurvey,
    companyStartDate
  });

  const timePeriodTransitionStepArray = isActivePeriod
    ? [timePeriodTransitionStep]
    : [];

  const commuteScheduleStep =
    commuteEndpoints.length > 1
      ? [
          {
            label: useYouForm
              ? translate("commuteSchedule.label")
              : "Commute Schedule",
            title: useYouForm
              ? translate("commuteSchedule.title")
              : `Great, now we need to know how often this employee works in each of these workspaces and how they get there.`,
            input: (
              <Typography variant="body1">
                {useYouForm
                  ? translate("commuteSchedule.instructions")
                  : `We've placed all of this employee's workspaces on this graphic to represent how many days per week this employee works in each one. The actual day of the week doesn't matter (e.g. Wednesday vs. Thursday), only how many days per week. Drag the arrows to adjust how often this employee works in each workspace and then move on to the next step.`}
              </Typography>
            ),
            onAdvanceForm: onAdvanceGraphicStep
          }
        ]
      : [];

  const commuteVehiclesStep = !!nonHomeEndpoints.length
    ? [
        {
          label: useYouForm
            ? translate("commuteVehicles.label")
            : "Commute Vehicles",
          title: useYouForm
            ? translate("commuteVehicles.title")
            : "Now we need to know which vehicles this employee uses to commute to each location.",
          input: <DragVehiclesStep survey={survey} />,
          buttonDisabled: !commuteEndpoints.reduce(
            (acc, { type, vehicles = [] }) => {
              if (type === "homeOffice") {
                return acc && true;
              }
              return acc && vehicles.length;
            },
            true
          )
        }
      ]
    : [];

  const forms = [
    {
      label: useYouForm ? translate("workspaces.label") : "Workspaces",
      title: endDate
        ? useYouForm
          ? translate("workspaces.titlePriorTo", { date: formatDate(endDate) })
          : `Now we need to know where this employee worked prior to ${formatDate(
              endDate
            )}`
        : useYouForm
          ? translate("workspaces.title")
          : "Now we need to know where this employee works and some information about each of those workspaces",
      rows: workspaceQuestions,
      formAdvanceErrorMessage: isNotValidWorkspaceTypeQuestions
        ? useYouForm
          ? translate("workspaces.validationError")
          : "You need to have at least one workplace selected before moving on."
        : "",
      onAdvanceForm: onAdvanceGraphicStep
    },
    {
      label: useYouForm ? translate("schedule.label") : "Work Schedule",
      title: useYouForm
        ? translate("schedule.title")
        : `We're going to use the graphic to the right to build this employee's commute schedule. A commute schedule includes the workspaces they're commuting to, the vehicles they're using to get there, and how often they travel to and work from each workspace.`,
      input: (
        <Typography variant="body1">
          {useYouForm
            ? translate("schedule.instructions")
            : `Let's start with how many days per week this employee works. Drag the slider on the chart to the right to adjust this employee's work schedule then move onto the next step.`}
        </Typography>
      ),
      onAdvanceForm: onDaysPerWeekNextStep
    },
    ...commuteScheduleStep,
    ...commuteVehiclesStep,
    ...timePeriodTransitionStepArray
  ];

  return (
    <>
      {!isActivePeriod && (
        <Grid item>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={mergeDarkTheme}>
              <Container maxWidth={formOpen ? "xl" : "md"}>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <Avatar
                          style={{
                            backgroundColor: theme.palette.employees.main
                          }}
                        >
                          <DoneIcon color="primary" />
                        </Avatar>
                      </Grid>
                      <Grid item>
                        <Typography variant="h5">{buildFormTitle()}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <IconButton
                      onClick={() =>
                        setFormOpen((currentStatus) => !currentStatus)
                      }
                      size="large"
                    >
                      {formOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Grid>
                </Grid>
              </Container>
            </ThemeProvider>
          </StyledEngineProvider>
        </Grid>
      )}
      {(isActivePeriod || formOpen) && (
        <Grid
          item
          container
          spacing={2}
          justifyContent="center"
          alignItems="center"
        >
          <Grid item sm={7} container direction="column" spacing={2}>
            <MultiPartFormReveal forms={forms} showAllSteps={!isActivePeriod} />
          </Grid>
          {!!graphicStep && (
            <Grid item sm={5} xs={12}>
              <DefaultPaper>
                <NewEmployeeWorkspacesGraphic
                  commuteEndpoints={commuteEndpoints}
                  editCommuteEndpoints={editCommuteEndpoints}
                  editCommuteEndpointsDirectly={editCommuteEndpointsDirectly}
                  updatePeriodEndpoints={updatePeriodEndpoints}
                  workSchedule={workSchedule}
                  setWorkSchedule={setWorkSchedule}
                  daysPerWk={daysPerWk}
                  graphicStep={graphicStep}
                  endDate={endDate}
                  home={home}
                  editTimePeriodHome={editTimePeriodHome}
                  editTimePeriod={editTimePeriod}
                />
              </DefaultPaper>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

const BuildWorkspacesForm = ({
  onSubmit,
  surveyThankYouStep,
  newCommuteScheduleSurvey,
  employeeData,
  modifiedEmployeeVehicles,
  employeeCommuteSurvey
}) => {
  const { newEmployee, setNewEmployee, employeeVehicles, setEmployeeVehicles } =
    useSharedNewEmployee();
  const [{ startDate: companyStartDate, isRemote }] =
    useSurveyBackendData("data");

  const {
    address: employeeAddress,
    homeSqFootage,
    realtyMolePropertyId
  } = employeeData || newEmployee;

  const [timePeriods, setTimePeriods] = useState([
    {
      tempId: generateTempId(),
      home: {
        address: employeeAddress,
        homeArea: homeSqFootage,
        realtyMolePropertyId
      }
    }
  ]);

  const [timePeriodStep, setTimePeriodStep] = useState(0);
  const [showSurveyThankYouStep, setShowSurveyThankYouStep] = useState(false);

  const editTimePeriod = (idx) => (field) => (value) =>
    editRowData(idx, setTimePeriods)(field, value);

  const editTimePeriodHomeProps = (periodTempId) => (field) => (value) =>
    setTimePeriods((currentPeriods) =>
      currentPeriods.map((period) => {
        if (period.tempId !== periodTempId) {
          return period;
        }
        const { home } = period;
        return { ...period, home: { ...home, [field]: value } };
      })
    );

  const editTimePeriodHome = (periodTempId) => (newHomeProps) =>
    setTimePeriods((currentPeriods) =>
      currentPeriods.map((period) => {
        if (period.tempId !== periodTempId) {
          return period;
        }
        const { home } = period;
        return { ...period, home: { ...home, ...newHomeProps } };
      })
    );

  const updatePeriodEndpoints = (periodTempId) => (updateFunction) =>
    setTimePeriods((currentPeriods) =>
      currentPeriods.map((period) => {
        if (periodTempId !== period.tempId) {
          return period;
        }
        const { commuteEndpoints } = period;
        return {
          ...period,
          commuteEndpoints: updateFunction(commuteEndpoints)
        };
      })
    );

  const addNewTimePeriod = () => {
    setTimePeriods((currentPeriods) => {
      const { startDate, home } = currentPeriods[timePeriodStep];
      const endDate = dayjs(startDate).subtract(1, "day");

      return [
        ...currentPeriods,
        {
          tempId: generateTempId(),
          endDate,
          home: { address: home.address }
        }
      ];
    });
    return setTimePeriodStep((currentStep) => currentStep + 1);
  };

  const submitFormattedTimePeriods = async () => {
    const formattedTimePeriods = await Promise.all(
      timePeriods.map(async (period) => {
        const { tempId, commuteEndpoints, home, ...otherPeriodProps } = period;
        const formattedCommuteEndpoints = commuteEndpoints.map(
          ({
            type,
            address,
            name,
            homeArea,
            homeOfficeArea,
            daysPerWk,
            vehicles,
            id
          }) => {
            if (type === "homeOffice") {
              return { type, name, homeArea, homeOfficeArea, daysPerWk };
            }

            const idObj = id ? { id } : {};

            return {
              type,
              address,
              name,
              daysPerWk,
              vehicles,
              ...idObj
            };
          }
        );

        const { homeArea, homeOfficeArea, address } = home;

        const eGrid = await fetchAddressEGrid(address);

        const homeAreaNumbersObj =
          homeArea && homeOfficeArea
            ? {
                homeArea: Number(homeArea),
                homeOfficeArea: Number(homeOfficeArea)
              }
            : {};

        return {
          commuteEndpoints: formattedCommuteEndpoints,
          home: { ...home, ...homeAreaNumbersObj, eGrid },
          ...otherPeriodProps
        };
      })
    );

    if (surveyThankYouStep) {
      setShowSurveyThankYouStep(true);
    }

    return onSubmit(formattedTimePeriods);
  };

  const timePeriodIsActive = (idx) => {
    if (showSurveyThankYouStep) {
      return false;
    }

    return timePeriodStep === idx;
  };

  useEffect(() => {
    if (
      !Object.keys(newEmployee).length &&
      (newCommuteScheduleSurvey || employeeCommuteSurvey)
    ) {
      setNewEmployee(employeeData);
    }

    if (!employeeVehicles.length && modifiedEmployeeVehicles?.length) {
      setEmployeeVehicles(modifiedEmployeeVehicles);
    }
  }, [
    employeeData,
    newCommuteScheduleSurvey,
    newEmployee,
    setNewEmployee,
    modifiedEmployeeVehicles,
    setEmployeeVehicles,
    employeeCommuteSurvey,
    employeeVehicles
  ]);

  return (
    <Grid container direction="column" spacing={4}>
      {timePeriods.map((timePeriod, idx) => (
        <>
          <TimePeriodGraphicForm
            key={`time-period-graphic-form-${idx}`}
            onSubmit={submitFormattedTimePeriods}
            editTimePeriod={editTimePeriod(idx)}
            addNewTimePeriod={addNewTimePeriod}
            updatePeriodEndpoints={updatePeriodEndpoints(timePeriod.tempId)}
            editTimePeriodHomeProps={editTimePeriodHomeProps(timePeriod.tempId)}
            editTimePeriodHome={editTimePeriodHome(timePeriod.tempId)}
            isActivePeriod={timePeriodIsActive(idx)}
            previousPeriod={timePeriods[idx - 1]}
            survey={surveyThankYouStep}
            newCommuteScheduleSurvey={newCommuteScheduleSurvey}
            employeeCommuteSurvey={employeeCommuteSurvey}
            companyStartDate={companyStartDate}
            isRemote={isRemote}
            {...timePeriod}
          />
          {idx < timePeriodStep && <FormDivider />}
        </>
      ))}
      {showSurveyThankYouStep && (
        <>
          <FormDivider />
          <Grid item container direction="column" spacing={4}>
            <MultiPartFormReveal forms={surveyThankYouStep} />
          </Grid>
        </>
      )}
    </Grid>
  );
};
export default BuildWorkspacesForm;
